import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { AlertTriangle, X } from 'lucide-react';

const SessionTimeoutWarning: React.FC = () => {
  const { sessionTimeout, resetSessionTimeout, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!sessionTimeout) return;

    const checkTimeout = () => {
      const now = Date.now();
      const timeRemaining = sessionTimeout - now;
      
      if (timeRemaining <= 0) {
        // Session expired
        logout();
        toast.error('Your session has expired. Please login again.');
        return;
      }

      if (timeRemaining <= 5 * 60 * 1000) { // 5 minutes warning
        setShowWarning(true);
        setTimeLeft(Math.ceil(timeRemaining / 1000));
      } else {
        setShowWarning(false);
      }
    };

    checkTimeout();
    const interval = setInterval(checkTimeout, 1000);

    return () => clearInterval(interval);
  }, [sessionTimeout, logout]);

  const handleExtendSession = () => {
    resetSessionTimeout();
    setShowWarning(false);
    toast.success('Session extended!');
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully.');
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!showWarning) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-yellow-800">
              Session Timeout Warning
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Your session will expire in {formatTime(timeLeft)}.</p>
              <p className="mt-1">Click "Extend Session" to stay logged in.</p>
            </div>
            <div className="mt-3 flex space-x-2">
              <button
                onClick={handleExtendSession}
                className="bg-yellow-600 text-white px-3 py-1 text-xs font-medium rounded hover:bg-yellow-700"
              >
                Extend Session
              </button>
              <button
                onClick={handleLogout}
                className="bg-gray-600 text-white px-3 py-1 text-xs font-medium rounded hover:bg-gray-700"
              >
                Logout
              </button>
            </div>
          </div>
          <div className="ml-3 flex-shrink-0">
            <button
              onClick={() => setShowWarning(false)}
              className="text-yellow-400 hover:text-yellow-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning; 